import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { countryCodes } from '../mockData';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', countryCode: '+1', contactNumber: '', subject: '', message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    alert('Thank you for reaching out! A member of our team will respond within one business day.');
    setFormData({ name: '', email: '', countryCode: '+1', contactNumber: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-[#58225a] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1739285452618-0b7b3d04f953?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGRlYWx8ZW58MHx8fHwxNzc2Mjg0ODk2fDA&ixlib=rb-4.1.0&q=85" alt="Business professionals greeting" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Get in Touch</h1>
            <p className="text-xl md:text-2xl text-gray-300">
              Questions about our services? Our team is ready to help you find the right path forward.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl font-bold text-[#58225a] mb-8">Reach Our Team</h2>
              <p className="text-lg text-gray-600 mb-8">Whether you have a quick question or need detailed guidance on a complex filing, we are here to assist.</p>
              <div className="space-y-6">
                {[
                  { icon: Mail, t: "Email Us", lines: ["info@trademarkexamininglawyer.com"] },
                  { icon: Phone, t: "Call Us", lines: ["+1 (877) 354-0074"] },
                  { icon: MapPin, t: "Visit Us", lines: ["527 W 7th St, Los Angeles, CA 90017"] },
                  { icon: Clock, t: "Office Hours", lines: ["Monday - Friday: 9:00 AM - 6:00 PM PST", "Saturday - Sunday: Closed"] }
                ].map((c, i) => (
                  <Card key={i} className="p-6 flex items-start space-x-4 hover:shadow-lg transition-shadow">
                    <div className="flex-shrink-0"><div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center"><c.icon className="h-6 w-6 text-white" /></div></div>
                    <div>
                      <h3 className="font-semibold text-[#58225a] mb-1">{c.t}</h3>
                      {c.lines.map((l, j) => <p key={j} className="text-gray-600">{l}</p>)}
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <Card className="p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-[#58225a] mb-6">Send a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-2" placeholder="Your full name" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-2" placeholder="your.email@example.com" />
                  </div>
                  <div>
                    <Label htmlFor="contact">Contact Number *</Label>
                    <div className="flex gap-2 mt-2">
                      <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{countryCodes.map((item) => (<SelectItem key={item.code} value={item.code}>{item.code}</SelectItem>))}</SelectContent>
                      </Select>
                      <Input id="contact" type="tel" required value={formData.contactNumber} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); if (v.length <= 10) setFormData({ ...formData, contactNumber: v }); }} placeholder="Phone number" className="flex-1" maxLength={10} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input id="subject" type="text" required value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="mt-2" placeholder="What is this regarding?" />
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea id="message" required value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="mt-2" placeholder="Provide details about your inquiry..." rows={5} />
                  </div>
                  <Button type="submit" className="w-full bg-[#ba2a84] hover:bg-[#8a1f63] py-6 text-lg font-semibold">Send Message</Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
